import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { apiClient } from '../lib/api';
import { Admin } from '../types';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import AdminModal from './AdminModal';
import { translatePermission, translateRole } from '../utils/translations';
import { AxiosError } from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

const AdminsList: React.FC = () => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState<Admin | undefined>(undefined);

  const { admin } = useAuth();
  if (!hasPermission(admin, 'admins', 'read')) {
    return <div className="text-red-600">У вас нет прав для просмотра администраторов.</div>;
  }

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error, refetch } = useQuery(
    ['admins', page, debouncedSearch, activeFilter],
    () => apiClient.getAdmins({
      page,
      limit: 10,
      search: debouncedSearch,
      active: activeFilter === 'true' ? true : activeFilter === 'false' ? false : undefined,
    })
  );

  const handleDelete = async (id: string) => {
    if (window.confirm('Вы уверены, что хотите удалить этого администратора?')) {
      try {
        await apiClient.deleteAdmin(id);
        toast.success('Администратор удален успешно');
        queryClient.invalidateQueries(['admins']);
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Ошибка удаления администратора');
      }
    }
  };

  const handleCreateAdmin = () => {
    setEditingAdmin(undefined);
    setIsModalOpen(true);
  };

  const handleEditAdmin = (admin: Admin) => {
    setEditingAdmin(admin);
    setIsModalOpen(true);
  };

  const handleSaveAdmin = async (adminData: Partial<Admin>) => {
    try {
      if (editingAdmin) {
        await apiClient.updateAdmin(editingAdmin.id, adminData as any);
        // Обновляем данные локально вместо полной перезагрузки
        queryClient.setQueryData(['admins', page, search, activeFilter], (oldData: any) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((admin: Admin) => 
              admin.id === editingAdmin.id ? { ...admin, ...adminData } : admin
            )
          };
        });
      } else {
        await apiClient.createAdmin(adminData as any);
        refetch(); // Для новых элементов нужна перезагрузка
      }
    } catch (error: any) {
      throw error;
    }
  };

  if (isLoading) return <div>Загрузка...</div>;
  if (error) {
    const status = (error as AxiosError)?.response?.status;
    if (status === 403) {
      return <div className="text-red-600">У вас нет прав для просмотра администраторов. Обратитесь к администратору.</div>;
    }
    return <div>Ошибка загрузки администраторов</div>;
  }

  const admins = data?.data || [];
  const pagination = data?.pagination;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Администраторы</h1>
        <button 
          onClick={handleCreateAdmin}
          className="btn btn-primary flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Создать администратора
        </button>
      </div>

      {/* Search */}
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="flex-1 min-w-0">
          <input
            type="text"
            placeholder="Поиск администраторов..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
        <div className="flex flex-col sm:flex-row gap-4 lg:flex-row">
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 min-w-[140px]"
          >
            <option value="">Все статусы</option>
            <option value="true">Активные</option>
            <option value="false">Неактивные</option>
          </select>
          <button
            onClick={() => {
              setSearch('');
              setActiveFilter('');
            }}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 min-w-[100px]"
          >
            Сбросить
          </button>
        </div>
      </div>

      {/* Admins Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Имя пользователя
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Роли
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Статус онлайн
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Последний вход
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {admins.map((admin: Admin) => (
                <tr key={admin.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {admin.username}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {admin.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex flex-wrap gap-1">
                        {admin.roles && admin.roles.map((role) => (
                          <span
                            key={role.id}
                            className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                          >
                            {translateRole(role)}
                          </span>
                        ))}
                      </div>
                      {admin.permissions && admin.permissions.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs text-gray-500">Индивидуальные права:</span>
                          {admin.permissions.map((permission) => (
                            <span
                              key={permission.id}
                              className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800"
                            >
                              {translatePermission(permission)}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      admin.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {admin.is_active ? 'Активен' : 'Неактивен'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${
                      admin.is_online ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      <div className={`w-2 h-2 rounded-full mr-1 ${
                        admin.is_online ? 'bg-green-500' : 'bg-gray-400'
                      }`}></div>
                      {admin.is_online ? 'Онлайн' : 'Оффлайн'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {admin.last_login ? new Date(admin.last_login).toLocaleString() : 'Никогда'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => handleEditAdmin(admin)}
                        className="text-primary-600 hover:text-primary-900"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(admin.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Предыдущая
              </button>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= pagination.pages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Следующая
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Показано <span className="font-medium">{((page - 1) * 10) + 1}</span> до{' '}
                  <span className="font-medium">
                    {Math.min(page * 10, pagination.total)}
                  </span>{' '}
                  из <span className="font-medium">{pagination.total}</span> результатов
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Предыдущая
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= pagination.pages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Следующая
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      <AdminModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        admin={editingAdmin}
        onSave={handleSaveAdmin}
      />
    </div>
  );
};

export default AdminsList; 